import { signal } from '@preact/signals';
import { useEffect, useMemo } from 'preact/hooks';
import { arabicKey } from '../audio/arabicVoice';
import { clipsReady, hasClip, loadManifest, recitationKeys, speak, speaking, stopNarration, unlockNarration } from '../audio/narrator';
import { settings } from '../state';
import { Icon } from './icons';

/** Which speaker started the reading now playing: the same words can show twice (the sky calendar repeats the crescent
 *  supplication), and only the one tapped shows as playing. */
const saidBy = signal(0);
let made = 0;

/** What a speaker needs for its clips: whether they are playing now, and its tap. Null where there is no recording. */
function useSpeaker(keys: string[]) {
  void loadManifest();
  const me = useMemo(() => ++made, []);
  const sig = keys.join(' ');
  // The reading ends when its card or page closes, or the voice is changed.
  useEffect(() => () => {
    if (speaking.peek()?.join(' ') === sig && saidBy.peek() === me) stopNarration();
  }, [sig]);
  if (!clipsReady.value || !keys.length || !keys.every(hasClip)) return null;
  const on = speaking.value?.join(' ') === sig && saidBy.value === me;
  const tap = () => {
    if (on) return stopNarration();
    saidBy.value = me;
    unlockNarration();
    void speak(keys, false);
  };
  return { on, tap };
}

/** An Arabic text in the voice chosen in Settings (src/audio/arabicVoice.ts); never a verse shown on its own. */
const useSay = (text: string) => useSpeaker([arabicKey(text, settings.value.arabicVoice)]);

/** A speaker beside an Arabic name (`name`, its transliteration, for the label) or under a hadith's or scholar's words. */
export function SayArabic({ text, name }: { text: string; name?: string }) {
  const say = useSay(text);
  if (!say) return null;
  return (
    <button type="button" class={say.on ? 'say-ar on' : 'say-ar'} aria-label={say.on ? 'Stop listening' : name ? `Hear ${name} in Arabic` : 'Hear it in Arabic'} onClick={say.tap}>
      <Icon name={say.on ? 'pause' : 'speaker'} size={16} />
    </button>
  );
}

/** A name in a list ("Also called"): where it has a recording, the name itself is the button, with a small speaker kept
 *  on the line of its last word. */
export function SayArabicName({ ar, tr }: { ar: string; tr: string }) {
  const say = useSay(ar);
  if (!say) {
    return (
      <span class="also-name">
        <span lang="ar" dir="rtl">{ar}</span> {tr}
      </span>
    );
  }
  const cut = tr.lastIndexOf(' ');
  return (
    <button type="button" class={say.on ? 'also-name say-name on' : 'also-name say-name'} aria-label={say.on ? 'Stop listening' : `Hear ${tr} in Arabic`} onClick={say.tap}>
      <span lang="ar" dir="rtl">{ar}</span> {tr.slice(0, cut + 1)}
      <span class="nowrap">{tr.slice(cut + 1)}<Icon name={say.on ? 'pause' : 'speaker'} size={14} /></span>
    </button>
  );
}

/** Under a verse shown on its own: al-Minshāwī's murattal recitation (DECISIONS 109), never the AI voice. A play
 *  symbol, not the speaker, so the recitation is told apart from the Arabic voice at a glance (the owner's choice). */
export function SayQuran({ text }: { text: string }) {
  const say = useSpeaker(recitationKeys(text));
  if (!say) return null;
  return (
    <button type="button" class={say.on ? 'say-ar say-quran on' : 'say-ar say-quran'} aria-label={say.on ? 'Stop listening' : 'Hear it recited by al-Minshāwī'} onClick={say.tap}>
      <Icon name={say.on ? 'pause' : 'play'} size={16} />
    </button>
  );
}
